/**
 * How the Enriched data tab words and dates what the service returns: OEM names, warranty and
 * check outcomes as badges, check origins, and dates as the design shows them ("4 Nov 2026").
 */
import type { CheckStatus, Oem, Product, WarrantyStatus } from './api';
import type { RowOutcome } from './bulk';

/** A badge's colours: ok green, warn amber, bad red, info blue, neutral grey. */
export type Tone = 'ok' | 'warn' | 'bad' | 'info' | 'neutral';

export const OEM_NAMES: Record<string, string> = {
  hp: 'HP',
  dell: 'Dell',
  lenovo: 'Lenovo',
  dynabook: 'Dynabook',
};

/** The OEM's name, from the answer, the code, or nothing. */
export function oemName(oem: Oem | string | null | undefined): string | null {
  if (!oem) return null;
  if (typeof oem === 'string') return OEM_NAMES[oem] ?? oem;
  return oem.name || (oem.code ? OEM_NAMES[oem.code] ?? oem.code : null);
}

/**
 * A product's name without the product number the OEM appends (`… PC (B0ZK9UP)`), since the
 * number shows on its own; the number when there is no name.
 */
export function productTitle(product: Product | null | undefined): string | null {
  const name = product?.product_name?.trim();
  const number = product?.product_number?.trim();
  if (name && number && name.endsWith(` (${number})`)) return name.slice(0, -(number.length + 3)).trim();
  return name || number || null;
}

/** A stored unit's warranty on the list. */
export const WARRANTY_BADGES: Record<WarrantyStatus, { label: string; tone: Tone }> = {
  active: { label: 'Active', tone: 'ok' },
  expired: { label: 'Expired', tone: 'bad' },
  not_started: { label: 'Not started', tone: 'warn' },
  unknown: { label: 'No coverage', tone: 'neutral' },
};

export const warrantyBadge = (status: string | null | undefined) =>
  WARRANTY_BADGES[status as WarrantyStatus] ?? WARRANTY_BADGES.unknown;

/** Why a checked serial is not stored (`check.status` of a needs-attention row). */
export const ATTENTION_BADGES: Record<Exclude<CheckStatus, 'success'>, { label: string; tone: Tone }> = {
  not_found: { label: 'Not found', tone: 'neutral' },
  oem_unknown: { label: 'OEM unknown', tone: 'neutral' },
  unsupported: { label: 'Unsupported', tone: 'neutral' },
  needs_more_info: { label: 'Needs model no.', tone: 'warn' },
  error: { label: 'Failed', tone: 'bad' },
};

export const attentionBadge = (status: string | null | undefined) =>
  ATTENTION_BADGES[status as keyof typeof ATTENTION_BADGES] ?? ATTENTION_BADGES.error;

/** A bulk row's outcome. */
export const OUTCOME_BADGES: Record<RowOutcome, { label: string; tone: Tone }> = {
  found: { label: 'Found', tone: 'ok' },
  not_found: { label: 'Not found', tone: 'neutral' },
  needs_info: { label: 'Needs model no.', tone: 'warn' },
  error: { label: 'Failed', tone: 'bad' },
};

/** How a check came about (`check.origin`), as the list's "Last checked" column says it. */
export const ORIGINS: Record<string, string> = {
  lookup: 'lookup',
  refresh: 'asked again',
  bulk_lookup: 'list lookup',
  file_import: 'import',
};

/** A batch's kind on the uploads list. */
export const KINDS: Record<string, string> = {
  bulk_lookup: 'List lookup',
  file_import: 'Imported answers',
};

export const BATCH_STATES: Record<string, { label: string; tone: Tone }> = {
  running: { label: 'Running', tone: 'info' },
  completed: { label: 'Completed', tone: 'ok' },
  cancelled: { label: 'Stopped', tone: 'warn' },
  failed: { label: 'Failed', tone: 'bad' },
};

const COUNT_LABELS: Record<string, string> = {
  stored: 'stored',
  found: 'found',
  not_found: 'not found',
  failed: 'failed',
  skipped: 'skipped',
};

/** "12 found · 3 not found" for the counts that are not zero, in a fixed order. */
export function countsText(counts: Record<string, number> | null | undefined, t: (key: string) => string): string {
  const order = [...Object.keys(COUNT_LABELS), ...Object.keys(counts ?? {}).filter((key) => !(key in COUNT_LABELS))];
  return order
    .filter((key) => (counts?.[key] ?? 0) > 0)
    .map((key) => `${(counts?.[key] ?? 0).toLocaleString()} ${t(COUNT_LABELS[key] ?? key)}`)
    .join(' · ');
}

/** "1 serial", "3 serials". */
export function serialCount(count: number, t: (key: string, values?: Record<string, unknown>) => string): string {
  return count === 1 ? t('1 serial') : t('{{n}} serials', { n: count.toLocaleString() });
}

const DAY = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const DAY_TIME = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

function toDate(at: string | Date | null | undefined): Date | null {
  if (!at) return null;
  const date = at instanceof Date ? at : new Date(at);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "4 Nov 2026", in the browser's time zone; '—' for nothing. */
export function shortDate(at: string | Date | null | undefined): string {
  const date = toDate(at);
  return date ? DAY.format(date) : (typeof at === 'string' && at) || '—';
}

/** "4 Nov 2026, 13:05". */
export function shortDateTime(at: string | null | undefined): string {
  const date = toDate(at);
  return date ? DAY_TIME.format(date) : at || '—';
}

/** `https://support.hp.com/us-en/…` → `support.hp.com`. */
export function sourceHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
